import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import type { LeadReport, ReportBucket } from "../types";

/**
 * Отчёт по выставке: сколько привезли, что это за клиенты, что стало сделками
 * и кто сколько принёс. Все цифры считаются из тех же карточек, что и список
 * лидов, — расхождений между отчётом и списком не бывает.
 */
export function ReportPage() {
  const [event, setEvent] = useState("");
  const [report, setReport] = useState<LeadReport | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .leadReport(event)
      .then((r) => {
        setReport(r);
        setError("");
      })
      .catch((e) => setError(e.message));
  }, [event]);

  const pct = (n: number, total: number) =>
    total ? `${Math.round((n / total) * 100)}%` : "0%";

  const period = (r: LeadReport) => {
    if (!r.period.from) return "";
    const f = (iso: string) =>
      new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
    const from = f(r.period.from);
    const to = r.period.to ? f(r.period.to) : from;
    return from === to ? from : `${from} — ${to}`;
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Отчёт по выставке</h1>
          <p>
            Сколько клиентов привезли, что это за клиенты, что стало сделками
            и кто сколько принёс.
          </p>
        </div>
        <div className="row no-print">
          <select value={event} onChange={(e) => setEvent(e.target.value)}>
            <option value="">Все выставки</option>
            {report?.events.map((ev) => (
              <option key={ev} value={ev}>
                {ev}
              </option>
            ))}
          </select>
          <button onClick={() => window.print()}>Распечатать</button>
        </div>
      </div>

      {error && <div className="banner red">{error}</div>}

      {!report ? (
        <div className="card">
          <div className="empty">
            <span className="spinner" /> Считаю…
          </div>
        </div>
      ) : report.total === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>Карточек пока нет</h3>
            <p>Отчёт появится, когда с выставки придут первые записи разговоров.</p>
          </div>
        </div>
      ) : (
        <>
          {period(report) && (
            <p className="muted small" style={{ marginTop: -6 }}>
              {report.event || "Все выставки"} · {period(report)}
            </p>
          )}

          <div className="kpi-row">
            <Kpi value={report.total} label="клиентов привезли" hero />
            <Kpi value={report.in_work} label="в работе" />
            <Kpi
              value={report.won}
              label="сделок"
              note={`конверсия ${pct(report.won, report.total)}`}
            />
            <Kpi value={report.hot} label="горячих" note={pct(report.hot, report.total)} />
            <Kpi
              value={report.stale}
              label="залежалось"
              note={`тишина ${report.stale_days}+ дн.`}
              link={report.stale > 0 ? "/leads?stale=1" : undefined}
            />
          </div>

          <div className="grid-2 report-grid">
            <ChartCard title="Воронка" hint="Где сейчас клиенты. Выделены сделки.">
              <BarList
                items={report.by_status}
                total={report.total}
                emphasis="won"
                keepOrder
              />
            </ChartCard>

            <ChartCard title="Направления" hint="Клиент с интересом к нескольким считается в каждом.">
              <BarList items={report.by_direction} total={report.total} />
            </ChartCard>

            <ChartCard title="Бренды">
              <BarList items={report.by_interest} total={report.total} />
            </ChartCard>

            <ChartCard title="Категории">
              <BarList items={report.by_category} total={report.total} keepOrder />
            </ChartCard>

            <ChartCard title="Тип клиента">
              <BarList items={report.by_client_type} total={report.total} />
            </ChartCard>

            <ChartCard title="Тёплость">
              <BarList items={report.by_temperature} total={report.total} keepOrder />
            </ChartCard>

            {report.by_city.length > 0 && (
              <ChartCard title="Города" hint="Первые десять.">
                <BarList items={report.by_city} total={report.total} />
              </ChartCard>
            )}
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Кто сколько привёз и ведёт</h3>
            </div>
            <div className="card-body muted small" style={{ paddingTop: 0 }}>
              «Привёз» — вёл переговоры на выставке. «Ведёт» — ответственный
              сейчас: после распределения и передач это часто другой человек.
            </div>
            <div className="table-wrap">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Сотрудник</th>
                    <th className="num">Привёз</th>
                    <th className="num">из них сделок</th>
                    <th className="num">Ведёт</th>
                    <th className="num">в работе</th>
                    <th className="num">сделок</th>
                    <th className="num">залежалось</th>
                  </tr>
                </thead>
                <tbody>
                  {report.people.map((p) => (
                    <tr key={p.user_id ?? "none"}>
                      <td>
                        <strong>{p.name}</strong>
                      </td>
                      <td className="num">{p.brought || "—"}</td>
                      <td className="num">{p.brought_won || "—"}</td>
                      <td className="num">{p.owns || "—"}</td>
                      <td className="num">{p.owns_in_work || "—"}</td>
                      <td className="num">{p.owns_won || "—"}</td>
                      <td className="num">
                        {p.owns_stale ? (
                          <span className="badge red">{p.owns_stale}</span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Kpi({
  value,
  label,
  note,
  hero,
  link,
}: {
  value: number;
  label: string;
  note?: string;
  hero?: boolean;
  link?: string;
}) {
  const body = (
    <>
      <div className={hero ? "kpi-value hero" : "kpi-value"}>{value}</div>
      <div className="kpi-label">{label}</div>
      {note && <div className="kpi-note">{note}</div>}
    </>
  );
  return link ? (
    <Link to={link} className="kpi-tile kpi-link">
      {body}
    </Link>
  ) : (
    <div className="kpi-tile">{body}</div>
  );
}

function ChartCard({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>{title}</h3>
      </div>
      <div className="card-body" style={{ paddingTop: 0 }}>
        {hint && <p className="muted small" style={{ marginTop: 0 }}>{hint}</p>}
        {children}
      </div>
    </div>
  );
}

/**
 * Горизонтальные полосы: одна мера, один цвет. Подписи и значения — текстом,
 * у каждой полосы свой всплывающий итог с долей от всех клиентов.
 */
function BarList({
  items,
  total,
  emphasis,
  keepOrder,
}: {
  items: ReportBucket[];
  total: number;
  /** Ключ полосы, которую выделяем цветом; остальные — приглушённые */
  emphasis?: string;
  /** Порядок из справочника осмысленный (статусы, A/B/C) — не пересортировывать */
  keepOrder?: boolean;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number; item: ReportBucket } | null>(null);

  const rows = keepOrder ? items : [...items].sort((a, b) => b.count - a.count);
  const max = Math.max(1, ...rows.map((r) => r.count));

  if (rows.every((r) => r.count === 0)) {
    return <p className="muted small">Данных пока нет.</p>;
  }

  const show = (e: React.PointerEvent | React.FocusEvent, item: ReportBucket) => {
    const box = wrap.current?.getBoundingClientRect();
    const target = (e.currentTarget as HTMLElement).getBoundingClientRect();
    if (!box) return;
    const x =
      "clientX" in e ? e.clientX - box.left : target.left - box.left + target.width / 2;
    setTip({ x, y: target.top - box.top, item });
  };

  return (
    <div className="bar-list" ref={wrap} onPointerLeave={() => setTip(null)}>
      {rows.map((item) => {
        const width = (item.count / max) * 100;
        const muted = emphasis !== undefined && item.key !== emphasis;
        return (
          <div
            key={item.key || "empty"}
            className="bar-row"
            tabIndex={0}
            onPointerMove={(e) => show(e, item)}
            onFocus={(e) => show(e, item)}
            onBlur={() => setTip(null)}
            aria-label={`${item.label}: ${item.count}`}
          >
            <span className="bar-label">{item.label}</span>
            <span className="bar-track">
              {item.count > 0 && (
                <span
                  className={muted ? "bar-fill muted-fill" : "bar-fill"}
                  style={{ width: `${Math.max(width, 2)}%` }}
                />
              )}
            </span>
            <span className="bar-value">{item.count}</span>
          </div>
        );
      })}

      {tip && (
        <div className="viz-tip" style={{ left: tip.x, top: tip.y }} role="status">
          <strong>{tip.item.count}</strong>
          <span>
            {tip.item.label} · {total ? Math.round((tip.item.count / total) * 100) : 0}% клиентов
          </span>
        </div>
      )}
    </div>
  );
}
